import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { DropdownChangeEvent } from 'primeng/dropdown';
import { capitalizeFirstLetter } from '../../../assets/functions/capitalize-first-letter';
import { clearZipCode } from '../../../assets/functions/format-specific-information-for-brazil';
import { validationCnpj, validationCpf } from '../../../assets/validations/cpf_cnpj_validation';
import { States } from '../../models/address/brazilian-states';
import { CepCityStateValidation } from '../../models/address/cep-city-state-validation';
import { ViaCep } from '../../models/address/via-cep';
import { ApsPermitedScreen } from '../../models/aps-permission/aps-permited-screen';
import { ClientData } from '../../models/client-data/client-data';
import { ConstantsFordProtect } from "../../models/constants/constants-ford-protect";
import { Patterns } from '../../models/constants/patterns';
import { SaleChannel } from '../../models/enum/sale-channel';
import { StatusChassi } from '../../models/enum/status-chassi';
import { VinValidate } from '../../models/vin-validate/vin-validate';
import { AddressService } from '../../services/address/address.service';
import { ValidationErrorMapperService } from '../../services/address/validation-error-mapper.service';
import { ApsPermissionService } from '../../services/aps-permission/aps-permission.service';
import { ClientDataService } from '../../services/client-data/client-data.service';
import { SystemCountryService } from '../../services/system-country/system-country.service';
import { VehicleInformationService } from '../../services/vehicle-information/vehicle-information.service';


@Component({
	selector: 'app-client-data',
	templateUrl: './client-data.component.html',
	styleUrls: ['./client-data.component.scss'],
	standalone: false
})
export class ClientDataComponent implements OnInit {
	private readonly destroyRef = inject(DestroyRef);

	validCustomerDocument: string | null = "";
	invalidCpf: string | null = "";
	invalidCnpj: string | null = "";
	invalidDocument: string | null = "";
	informedCustomerDocument = "";
	customerForm: UntypedFormGroup = null!;
	clientData: ClientData;
	saleChannel: SaleChannel;
	systemCountry: string = "";
	countryPlg: string = "";
	minLenghtDocument: number = 0;
	maxLenghtDocument: number = 0;
	phoneMinLength = 11;
	phoneMaxLength = 11;
	zipMinLength = 8;
	zipMaxLength = 8;

	nameMaxLength = 49;
	nameMaxLengths: Record<string, number> = {
		ARG: 100,
		CHL: 100,
		COL: 100,
		BRA: 49
	};

	addressViaCep: ViaCep;
	maxBirthDate: Date;

	propertyNumberMaxLength = 20;
	propertyNumberMaxLengths: Record<string, number> = {
		ARG: 20,
		CHL: 10,
		COL: 10,
		BRA: 20
	};

	stateNumberMaxLength = 2;
	stateNumberMaxLengths: Record<string, number> = {
		ARG: 30,
		CHL: 30,
		COL: 30,
		BRA: 2
	};

	vinValidate: VinValidate = null!;
	emptyCustomerDocument = false;
	blockedUi = false;
	statesList: { label: string, value: string }[] = [];
	citiesList: { label: string, value: string }[] = [];
	selectedState: string | null = "";
	selectedCity: string | null = "";
	disableState = false;
	disableCity = false;
	renderCityDropdown = true;
	showRegistrationForm = false;
	showLocationRequiredErrors = false;
	locationValidationErrorKey: string | null = null;
	isZipLookupInProgress = false;
	private zipSearchRequestId = 0;
	private citySearchRequestId = 0;
	private readonly consultDebounceMs = 200;
	private consultDebounceHandle: ReturnType<typeof setTimeout> | null = null;
	private readonly zipLookupDebounceMs = 350;
	private zipLookupDebounceHandle: ReturnType<typeof setTimeout> | null = null;
	private lastLoadedOrConsultedDocument = "";
	private locationLockedByDocument = false;
	private lockedLocationZip = "";
	isCtaSuccess = false;
	private formChangeDebounceTimer: ReturnType<typeof setTimeout> | null = null;

	constructor(

		private vehicleInformationService: VehicleInformationService,
		private fb: UntypedFormBuilder,
		private router: Router,
		private route: ActivatedRoute,
		private addressService: AddressService,
		private clientDataService: ClientDataService,
		private translateService: TranslateService,
		private messageService: MessageService,
		private validationErrorMapperService: ValidationErrorMapperService,
		private apsPermissionService: ApsPermissionService,
		private systemCountryService: SystemCountryService,
	) {

		this.clientData = new ClientData();
		this.saleChannel = route.snapshot.paramMap.get('saleChannel') as SaleChannel;
		this.systemCountryService.setSystemCountry();
		const today = new Date();
		this.addressViaCep = new ViaCep();

		this.maxBirthDate = new Date(
			today.getFullYear() - 18,
			today.getMonth(),
			today.getDate()
		);

	}

	ngOnInit() {

		this.vinValidate = this.vehicleInformationService.getAllChassiValidation();
		if (this.vinValidate === null || this.vinValidate.validated === StatusChassi.invalid) {
			this.router.navigate(['sales-channel', this.saleChannel], { skipLocationChange: true, replaceUrl: false });
		}

		this.systemCountry = this.systemCountryService.getSystemCountry();
		this.nameMaxLength = this.nameMaxLengths[this.systemCountry];
		this.countryPlg = 'p-lg-6';
		this.propertyNumberMaxLength = this.propertyNumberMaxLengths[this.systemCountry];
		this.stateNumberMaxLength = this.stateNumberMaxLengths[this.systemCountry];

		let cepFormControl: UntypedFormControl;
		let birthDateFormControl: UntypedFormControl;

		switch (this.systemCountry) {

			case ConstantsFordProtect.COL:

				this.minLenghtDocument = 4;
				this.maxLenghtDocument = 10;
				this.phoneMinLength = 7;
				this.phoneMaxLength = 12;
				this.zipMinLength = 0;
				this.zipMaxLength = 8;
				cepFormControl = new UntypedFormControl('', [Validators.minLength(this.zipMinLength), Validators.maxLength(this.zipMaxLength), Validators.pattern(Patterns.ONLY_NUMBERS)]);
				birthDateFormControl = new UntypedFormControl(null);
				this.customerForm = this.formControlGenerator(this.phoneMinLength, this.phoneMaxLength, cepFormControl, birthDateFormControl);
				break;

			case ConstantsFordProtect.CHL:

				this.minLenghtDocument = 9;
				this.maxLenghtDocument = 9;
				this.phoneMinLength = 11;
				this.phoneMaxLength = 11;
				this.zipMinLength = 0;
				this.zipMaxLength = 10;
				cepFormControl = new UntypedFormControl('', [Validators.minLength(this.zipMinLength), Validators.maxLength(this.zipMaxLength), Validators.pattern(Patterns.ONLY_NUMBERS)]);
				birthDateFormControl = new UntypedFormControl(null);
				this.customerForm = this.formControlGenerator(this.phoneMinLength, this.phoneMaxLength, cepFormControl, birthDateFormControl);
				break;

			case ConstantsFordProtect.ARG:

				this.minLenghtDocument = 7;
				this.maxLenghtDocument = 11;
				this.phoneMinLength = 7;
				this.phoneMaxLength = 14;
				this.zipMinLength = 4;
				this.zipMaxLength = 20;
				cepFormControl = new UntypedFormControl('', [Validators.required, Validators.minLength(this.zipMinLength), Validators.maxLength(this.zipMaxLength), Validators.pattern(Patterns.ONLY_NUMBERS)]);
				birthDateFormControl = new UntypedFormControl(null);

				this.customerForm = this.formControlGenerator(this.phoneMinLength, this.phoneMaxLength, cepFormControl, birthDateFormControl);
				break;

			default:

				this.minLenghtDocument = 11;
				this.maxLenghtDocument = 14;
				this.phoneMinLength = 11;
				this.phoneMaxLength = 11;
				this.zipMinLength = 8;
				this.zipMaxLength = 8;
				cepFormControl = new UntypedFormControl('', [Validators.required, Validators.minLength(this.zipMinLength), Validators.maxLength(this.zipMaxLength), Validators.pattern(Patterns.ONLY_NUMBERS)]);
				birthDateFormControl = new UntypedFormControl(null, [Validators.required, Validators.minLength(10)]);
				this.customerForm = this.formControlGenerator(this.phoneMinLength, this.phoneMaxLength, cepFormControl, birthDateFormControl);
				break;

		}

		this.apsPermissionService.permissionValidation(ApsPermitedScreen.screenCustomerValidation);

		console.log("[client-data]init[0] -> Client Data: ", this.clientDataService.getClientData() === null ? "Client Data is null" : "Client Data is not null");
		console.log("[client-data]init[1] -> Client Data country code: ", this.clientDataService.getClientData().countryCode);
		if (this.clientDataService.getClientData() !== null) {

			this.clientData = this.clientDataService.getClientData();
		}

		console.log("[client-data]init[2] -> Client Data country code: ", this.clientData.countryCode);
		if (this.clientData.countryCode === null) {
			console.log("[client-data]init[3] -> this.systemCountryService.getSystemCountry(): ", this.systemCountryService.getSystemCountry());
			this.clientData.countryCode = this.systemCountryService.getSystemCountry();
		}

		if (this.clientData.complement === null) {
			this.clientData.complement = "";
		}

		if (this.clientData.nationalId !== null) {
			this.informedCustomerDocument = this.clientData.nationalId;

			const consultState = this.clientDataService.getConsultState();
			const normalizedDocument = this.normalizeDocument(this.clientData.nationalId);
			const hasConsultForCurrentDocument = consultState.performed && consultState.document === normalizedDocument;

			this.lastLoadedOrConsultedDocument = hasConsultForCurrentDocument ? normalizedDocument : "";
			this.showRegistrationForm = hasConsultForCurrentDocument;
		}

		this.updateCustomerNameValidation();

		console.log("[client-data] onInit -> Client Data country code: ", this.clientData.countryCode);
		console.log("[client-data] onInit -> Client Data country code via service: ", this.clientDataService.getClientData().countryCode);
		(document.getElementById("customerDocument") as HTMLInputElement).focus();
		this.selectedState = this.clientData.stateCode || "";
		this.disableCity = this.shouldUseBrazilAddressValidation() && !this.selectedState;
		const allowLocationAdjustment = this.clientDataService.consumeLocationAdjustmentRequired();

		const hasPersistedBrazilLocation = this.shouldUseBrazilAddressValidation()
			&& Boolean(this.clientData.stateCode)
			&& Boolean(this.clientData.city);

		if (hasPersistedBrazilLocation && !allowLocationAdjustment) {
			this.locationLockedByDocument = true;
			this.lockedLocationZip = this.normalizeZip(this.clientData?.zip);
			this.disableState = true;
			this.disableCity = true;
			this.clientDataService.setLocationLockState(true, this.lockedLocationZip);
		} else if (allowLocationAdjustment) {
			this.locationLockedByDocument = false;
			this.lockedLocationZip = '';
			this.disableState = false;
			this.disableCity = this.shouldUseBrazilAddressValidation() && !this.selectedState;
			this.clientDataService.clearLocationLockState();
		}

		// Restore location lock state from service BEFORE loading cities
		// This ensures keepCityDisabledAfterLoad flag is set before async searchingCities completes
		const locationLockState = this.clientDataService.getLocationLockState();
		if (!allowLocationAdjustment && locationLockState.locked && locationLockState.zipCode) {
			this.locationLockedByDocument = true;
			this.lockedLocationZip = locationLockState.zipCode;
			this.disableState = true;
			this.disableCity = true;
		}

		this.loadStates();

		// Load cities AFTER restoring lock state, passing the lock flag to maintain disabled state
		if (this.isBrazilCountry(this.clientData.countryCode || this.systemCountry) && this.clientData.stateCode && this.clientData.city) {
			this.searchingCities(this.clientData.stateCode, this.clientData.city, this.locationLockedByDocument);
		}

		this.updateAddressControlsState();

		// Listen to form changes for CTA success state
		this.customerForm.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => this.onFormChange());

	}

	onFormChange() {
		if (this.formChangeDebounceTimer) {
			clearTimeout(this.formChangeDebounceTimer);
		}
		this.isCtaSuccess = false;

		this.formChangeDebounceTimer = setTimeout(() => {
			this.isCtaSuccess = this.customerForm?.valid ?? false;
		}, 700);
	}

	private updateAddressControlsState(): void {

		if (!this.customerForm) {
			return;
		}

		const stateControl = this.customerForm.get('optionState');
		const cityControl = this.customerForm.get('optionCity');

		if (stateControl) {
			if (this.disableState) {
				stateControl.disable({ emitEvent: false });
			} else {
				stateControl.enable({ emitEvent: false });
			}
		}

		if (cityControl) {
			if (this.disableCity) {
				cityControl.disable({ emitEvent: false });
			} else {
				cityControl.enable({ emitEvent: false });
			}
		}
	}

	updateCountryPlgBasedOnLength(inputValue: string): void {

		const length = inputValue.length;

		if (length <= 49) {
			this.countryPlg = 'p-lg-6';
		} else {
			this.countryPlg = 'p-lg-10';
		}

	}

	onNameFieldInput(event: Event): void {
		if (this.shouldAllowDigitsInCustomerName()) {
			this.syncFieldValue(event, 'inputName', 'firstname', this.nameMaxLength);
		} else {
			this.removeDigitsFromLettersFieldInput(event, 'inputName', 'firstname', this.nameMaxLength);
		}
		this.updateCountryPlgBasedOnLength(this.clientData.firstname || '');
	}

	preventInvalidCustomerNameDigits(event: KeyboardEvent): void {
		if (this.shouldAllowDigitsInCustomerName()) {
			return;
		}

		this.preventDigitsInLettersFields(event);
	}

	handleCustomerNamePaste(event: ClipboardEvent): void {
		if (this.shouldAllowDigitsInCustomerName()) {
			return;
		}

		this.handleLettersFieldPaste(event, 'inputName', 'firstname', this.nameMaxLength);
	}

	private requiredTrimmedTextValidator(): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			const value = `${control.value ?? ''}`;

			if (!value) {
				return null;
			}

			return value.trim().length === 0 ? { whitespace: true } : null;
		};
	}

	private formControlGenerator(phoneMinlength: number, phoneMaxlength: number, cepValidator: UntypedFormControl, birthDate: UntypedFormControl) {

		const isBrazil = this.systemCountry === ConstantsFordProtect.BRA;
		const requiredTrimmedText = this.requiredTrimmedTextValidator();

		return this.fb.group({
			'inputName': new UntypedFormControl('', [Validators.required, requiredTrimmedText, Validators.minLength(2), Validators.maxLength(this.nameMaxLength), Validators.pattern(this.getCustomerNamePattern())]),
			'inputLastName': new UntypedFormControl('', [Validators.required, requiredTrimmedText, Validators.minLength(2), Validators.maxLength(50), Validators.pattern(Patterns.LETTERS_WITH_ACCENTS)]),
			'inputSocialName': new UntypedFormControl(null, [Validators.maxLength(50), Validators.pattern(Patterns.LETTERS_WITH_ACCENTS)]),
			'inputBirthDate': birthDate,
			'inputPhoneNumber': new UntypedFormControl('', [Validators.required, Validators.minLength(phoneMinlength), Validators.maxLength(phoneMaxlength), Validators.pattern(Patterns.ONLY_NUMBERS)]),
			'inputEmail': new UntypedFormControl('', [Validators.required, requiredTrimmedText, Validators.maxLength(80), Validators.pattern(Patterns.EMAIL_STRICT)]),
			'inputZipCode': cepValidator,
			'inputAddress': new UntypedFormControl('', [Validators.required, requiredTrimmedText, Validators.maxLength(200), Validators.pattern(Patterns.LETTERS_NUMBERS_AND_PUNCTUATION)]),
			'inputNumber': new UntypedFormControl('', [Validators.required, requiredTrimmedText, Validators.minLength(2), Validators.maxLength(this.propertyNumberMaxLength), Validators.pattern(Patterns.ONLY_NUMBERS)]),
			'inputDistrict': new UntypedFormControl('', [Validators.required, requiredTrimmedText, Validators.maxLength(100), Validators.pattern(Patterns.LETTERS_NUMBERS_AND_PARENTS)]),
			'inputComplement': new UntypedFormControl(''),
			'inputCity': new UntypedFormControl('',
				isBrazil
					? []
					: [Validators.required, requiredTrimmedText, Validators.maxLength(50), Validators.pattern(Patterns.LETTERS_WITH_ACCENTS)]
			),
			'inputState': new UntypedFormControl('',
				isBrazil
					? []
					: [Validators.required, requiredTrimmedText, Validators.pattern(Patterns.LETTERS_WITH_ACCENTS), Validators.minLength(2), Validators.maxLength(this.stateNumberMaxLength)]
			),
			'optionState': new UntypedFormControl('',
				isBrazil
					? [Validators.required, Validators.minLength(2), Validators.maxLength(this.stateNumberMaxLength)]
					: []
			),
			'optionCity': new UntypedFormControl('',
				isBrazil
					? [Validators.required, Validators.maxLength(50)]
					: []
			),
		});

	}

	private isBrazilCountry(countryCode?: string): boolean {
		return countryCode === ConstantsFordProtect.BRA;
	}

	private shouldAllowDigitsInCustomerName(): boolean {
		return this.isBrazilCountry(this.systemCountry) && this.normalizeDocument(this.informedCustomerDocument).length === 14;
	}

	private getCustomerNamePattern(): string {
		return this.shouldAllowDigitsInCustomerName()
			? Patterns.LETTERS_NUMBERS_WITH_ACCENTS
			: Patterns.LETTERS_WITH_ACCENTS;
	}

	private updateCustomerNameValidation(): void {
		const control = this.customerForm.get('inputName');

		if (!control) {
			return;
		}

		control.setValidators([
			Validators.required,
			this.requiredTrimmedTextValidator(),
			Validators.minLength(2),
			Validators.maxLength(this.nameMaxLength),
			Validators.pattern(this.getCustomerNamePattern())
		]);

		if (!this.shouldAllowDigitsInCustomerName()) {
			const currentValue = `${control.value || ''}`;
			const sanitizedValue = currentValue.replace(/\d+/g, '').slice(0, this.nameMaxLength);

			if (sanitizedValue !== currentValue) {
				control.setValue(sanitizedValue, { emitEvent: false });
				this.clientData.firstname = sanitizedValue;
			}
		}

		control.updateValueAndValidity({ emitEvent: false });
		this.updateCountryPlgBasedOnLength(this.clientData.firstname || '');
	}

	private shouldUseBrazilAddressValidation(): boolean {
		return this.isBrazilCountry(this.clientData?.countryCode || this.systemCountry);
	}

	get shouldShowZipLookupProgress(): boolean {
		return this.shouldUseBrazilAddressValidation() && this.isZipLookupInProgress;
	}

	private setZipLookupInProgress(inProgress: boolean): void {
		this.isZipLookupInProgress = inProgress;
		this.updateZipLookupBlockingState();
	}

	private updateZipLookupBlockingState(): void {
		if (!this.customerForm) {
			return;
		}

		const addressControls = ['inputAddress', 'inputNumber', 'inputDistrict', 'inputComplement', 'inputCity', 'inputState'];

		for (const controlName of addressControls) {
			const control = this.customerForm.get(controlName);
			if (!control) {
				continue;
			}

			if (this.shouldShowZipLookupProgress) {
				control.disable({ emitEvent: false });
			} else {
				control.enable({ emitEvent: false });
			}
		}

		if (this.shouldShowZipLookupProgress) {
			this.customerForm.get('optionState')?.disable({ emitEvent: false });
			this.customerForm.get('optionCity')?.disable({ emitEvent: false });
			return;
		}

		this.updateAddressControlsState();
	}

	private cancelZipLookupDebounce(): void {
		if (this.zipLookupDebounceHandle) {
			clearTimeout(this.zipLookupDebounceHandle);
			this.zipLookupDebounceHandle = null;
		}
	}

	onKeyUpAddress(event: KeyboardEvent): void {

		if (!this.shouldUseBrazilAddressValidation()) {
			return;
		}

		if ((this.clientData.zip !== "") && (this.clientData.zip !== null)) {
			if (event.key !== "Control" && this.clientData.zip.length >= 8) {
				this.cancelZipLookupDebounce();
				const newZip = this.clientData.zip;
				this.searchAddress(newZip.replace("-", ""));
			}
		}
	}

	onZipInputChange(): void {

		if (!this.shouldUseBrazilAddressValidation()) {
			return;
		}

		this.showLocationRequiredErrors = false;
		this.clearLocationValidationError();
		this.unlockLocationWhenZipChanges();
		this.invalidatePendingAddressLookups();
		this.cancelZipLookupDebounce();

		const hasResolvedAddressData = Boolean(
			this.clientData.address ||
			this.clientData.district ||
			this.clientData.number ||
			this.clientData.complement ||
			this.clientData.stateCode ||
			this.clientData.city ||
			this.selectedState ||
			this.selectedCity ||
			this.citiesList.length
		);

		if (!this.clientData.zip) {
			this.setZipLookupInProgress(false);
			this.clearAddressSearches(false);
			return;
		}

		const normalizedZip = this.normalizeZip(this.clientData.zip);
		if (normalizedZip.length < 8) {
			this.setZipLookupInProgress(false);
			if (hasResolvedAddressData) {
				this.clearAddressSearches(false);
			}
			return;
		}

		this.setZipLookupInProgress(true);
		
		if (hasResolvedAddressData) {
			this.clearAddressSearches(false);
		}

		this.zipLookupDebounceHandle = setTimeout(() => {
			const currentZip = this.normalizeZip(this.clientData.zip);
			if (currentZip.length < 8) {
				this.setZipLookupInProgress(false);
				return;
			}

			this.searchAddress(currentZip);
		}, this.zipLookupDebounceMs);
	}

	private resetCityDropdownView(): void {
		// Passo 1: habilita o FormControl temporariamente para que o valor "" seja
		// propagado pelo ngModel ao PrimeNG (controle desabilitado bloqueia re-render)
		const cityControl = this.customerForm?.get('optionCity');
		cityControl?.enable({ emitEvent: false });
		cityControl?.setValue('', { emitEvent: false });

		// Passo 2: destrói o dropdown (Angular precisa detectar a mudança)
		this.renderCityDropdown = false;

		// Passo 3: setTimeout garante um ciclo completo de change detection antes
		// de recriar, diferente de Promise.resolve() que pode ser batched pela Zone
		setTimeout(() => {
			this.renderCityDropdown = true;
			// Passo 4: só desabilita DEPOIS que o novo dropdown foi criado com valor vazio
			this.updateAddressControlsState();
		}, 0);
	}

	private invalidatePendingAddressLookups(): void {
		this.zipSearchRequestId += 1;
		this.citySearchRequestId += 1;
		this.setZipLookupInProgress(false);
	}

	private normalizeZip(zip: string | null | undefined): string {
		return (zip || '').replace(/\D/g, '');
	}

	private lockLocationFromLoadedDocument(): void {
		this.locationLockedByDocument = true;
		this.lockedLocationZip = this.normalizeZip(this.clientData?.zip);
		this.disableState = true;
		this.disableCity = true;
		// Persist lock state in service for navigation scenarios
		this.clientDataService.setLocationLockState(true, this.lockedLocationZip);
		this.updateAddressControlsState();
	}

	private unlockLocationWhenZipChanges(): void {
		if (!this.locationLockedByDocument) {
			return;
		}

		const currentZip = this.normalizeZip(this.clientData?.zip);
		if (currentZip === this.lockedLocationZip) {
			this.disableState = true;
			this.disableCity = true;
			this.updateAddressControlsState();
			return;
		}

		this.locationLockedByDocument = false;
		this.lockedLocationZip = '';
		// Clear lock state in service when user changes the zip
		this.clientDataService.clearLocationLockState();
	}

	allowOnlyDigits(event: KeyboardEvent): void {
		const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];

		if (allowedKeys.includes(event.key) || event.ctrlKey || event.metaKey) {
			return;
		}

		if (!/^[0-9]$/.test(event.key)) {
			event.preventDefault();
		}
	}

	preventDigitsInLettersFields(event: KeyboardEvent): void {
		const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];

		if (allowedKeys.includes(event.key) || event.ctrlKey || event.metaKey) {
			return;
		}

		if (/^[0-9]$/.test(event.key)) {
			event.preventDefault();
		}
	}

	removeDigitsFromLettersFieldInput(event: Event, controlName: string, modelProperty: keyof ClientData, maxLength?: number): void {
		const input = event.target as HTMLInputElement;
		const sanitizedRawValue = (input.value || '').replace(/\d+/g, '');
		const sanitizedValue = typeof maxLength === 'number'
			? sanitizedRawValue.slice(0, maxLength)
			: sanitizedRawValue;

		if (input.value !== sanitizedValue) {
			input.value = sanitizedValue;
		}

		this.customerForm.get(controlName)?.setValue(sanitizedValue, { emitEvent: false });
		(this.clientData[modelProperty] as string) = sanitizedValue;
	}

	private syncFieldValue(event: Event, controlName: string, modelProperty: keyof ClientData, maxLength?: number): void {
		const input = event.target as HTMLInputElement;
		const sanitizedValue = typeof maxLength === 'number'
			? (input.value || '').slice(0, maxLength)
			: (input.value || '');

		if (input.value !== sanitizedValue) {
			input.value = sanitizedValue;
		}

		this.customerForm.get(controlName)?.setValue(sanitizedValue, { emitEvent: false });
		(this.clientData[modelProperty] as string) = sanitizedValue;
	}

	handleLettersFieldPaste(event: ClipboardEvent, controlName: string, modelProperty: keyof ClientData, maxLength?: number): void {
		const pastedText = event.clipboardData?.getData('text') || '';
		if (!/\d/.test(pastedText)) {
			return;
		}

		event.preventDefault();

		const input = event.target as HTMLInputElement;
		const start = input.selectionStart ?? input.value.length;
		const end = input.selectionEnd ?? input.value.length;
		const sanitizedPaste = pastedText.replace(/\d+/g, '');
		const nextValue = `${input.value.slice(0, start)}${sanitizedPaste}${input.value.slice(end)}`;
		const sanitizedValue = typeof maxLength === 'number'
			? nextValue.slice(0, maxLength)
			: nextValue;

		input.value = sanitizedValue;
		this.customerForm.get(controlName)?.setValue(sanitizedValue, { emitEvent: false });
		(this.clientData[modelProperty] as string) = sanitizedValue;
	}

	handlePhonePaste(event: ClipboardEvent): void {
		const pastedText = event.clipboardData?.getData('text') || '';
		if (/\D/.test(pastedText)) {
			event.preventDefault();
		}
	}

	onZipInput(event: Event): void {
		const input = event.target as HTMLInputElement;
		const numericValue = (input.value || '').replace(/\D+/g, '').slice(0, this.zipMaxLength);

		if (input.value !== numericValue) {
			input.value = numericValue;
		}

		this.clientData.zip = numericValue;
		this.customerForm.get('inputZipCode')?.setValue(numericValue, { emitEvent: false });
		this.customerForm.get('inputZipCode')?.updateValueAndValidity({ emitEvent: false });
		this.onZipInputChange();
	}

	enforceNumericPhoneInput(event: Event): void {
		const input = event.target as HTMLInputElement;
		const numericValue = (input.value || '').replace(/\D+/g, '').slice(0, this.phoneMaxLength);

		if (input.value !== numericValue) {
			input.value = numericValue;
		}

		this.customerForm.get('inputPhoneNumber')?.setValue(numericValue, { emitEvent: false });
		this.customerForm.get('inputPhoneNumber')?.updateValueAndValidity({ emitEvent: false });
	}

	loadStates(): void {

		if (!this.shouldUseBrazilAddressValidation()) {
			this.statesList = [];
			return;
		}

		this.addressService.getStates()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((data: States[]) => {
				this.statesList = data.map(state => ({ label: state.stateName, value: state.stateUf }));
			});
	}

	searchAddress(zipCode: string) {

		const zipRequestId = ++this.zipSearchRequestId;
		this.citySearchRequestId += 1;
		this.clearAddressSearches(false);
		this.showLocationRequiredErrors = false;
		this.clientData.countryCode = this.systemCountry;

		if (!this.shouldUseBrazilAddressValidation()) {
			this.setZipLookupInProgress(false);
			this.updateAddressControlsState();
			return;
		}

		if (this.systemCountry === ConstantsFordProtect.BRA) {
			this.setZipLookupInProgress(true);

			this.addressService.getViaCep(zipCode)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe({
					next: (response: ViaCep) => {

						if (zipRequestId !== this.zipSearchRequestId) {
							return;
						}

						this.setZipLookupInProgress(false);

						if (response === null || response.postalCode === null ||
							response.postalCode === "") {

							this.messageService.add({
								severity: 'warn',
								summary: this.translateService.instant('TRANSLATE.ALERT'),
								detail: this.translateService.instant('TRANSLATE.ADDRESS.NOT.FOUND')
							});

						} else {

							this.addressViaCep = response;

							this.clientData.countryCode = ConstantsFordProtect.BRA;
							this.clientData.address = capitalizeFirstLetter(this.addressViaCep.street);
							this.clientData.district = capitalizeFirstLetter(this.addressViaCep.district);
							this.clientData.number = "";
							this.clientData.complement = "";

							if (this.addressViaCep.postalCode) {

								this.clientData.zip = clearZipCode(this.addressViaCep.postalCode);
							} else {
								this.clientData.zip = clearZipCode(zipCode);
							}

							const statusExists = this.statesList.find(statusFound =>
								statusFound.value === this.addressViaCep.stateAbbreviation);

							if (statusExists) {

								this.selectedState = this.addressViaCep.stateAbbreviation;
								this.clientData.stateCode = this.selectedState;
								this.disableState = true;
							} else {
								this.selectedState = "";
								this.disableState = false;
								this.disableCity = true;
								this.citiesList = [];
							}

							if (this.addressViaCep.stateAbbreviation &&
								this.addressViaCep.city
							) {
								this.disableCity = true;
								this.searchingCities(this.addressViaCep.stateAbbreviation, this.addressViaCep.city, true);
							}

							this.updateAddressControlsState();

						}

					}, error: (error) => {

						if (zipRequestId !== this.zipSearchRequestId) {
							return;
						}

						this.setZipLookupInProgress(false);

						// Limpar modelo e FormControls quando CEP não for encontrado
						this.clientData.stateCode = "";
						this.clientData.city = "";
						this.clientData.address = "";
						this.clientData.district = "";
						this.clientData.number = "";
						this.clientData.complement = "";
						this.selectedState = "";
						this.selectedCity = "";
						this.citiesList = [];
						this.disableCity = true;
						this.disableState = false;

						// Resetar FormControls EXPLICITAMENTE para sincronizar PrimeNG
						this.customerForm?.patchValue({
							optionState: '',
							optionCity: ''
						}, { emitEvent: false });

						// Marcar como não tocado para limpar validação visual
						const stateControl = this.customerForm?.get('optionState');
						const cityControl = this.customerForm?.get('optionCity');

						if (stateControl) {
							stateControl.markAsPristine();
							stateControl.markAsUntouched();
						}

						if (cityControl) {
							cityControl.markAsPristine();
							cityControl.markAsUntouched();
						}

						// Forçar recriação do dropdown para limpar estado interno do PrimeNG
						this.resetCityDropdownView();

						switch (error.status) {
							case 404:
							case 500:

								this.messageService.add({
									severity: 'error',
									summary: this.translateService.instant('TRANSLATE.ERROR'),
									detail: this.translateService.instant('TRANSLATE.ZIP.NOT.FOUND')
								});
								break;

							case 401:
								this.messageService.add({
									severity: 'error',
									summary: this.translateService.instant('TRANSLATE.ERROR'),
									detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
								});
								break;

							default:
								this.messageService.add({
									severity: 'error',
									summary: this.translateService.instant('TRANSLATE.ERROR'),
									detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
								});
								break;
						}
					}
				});
		}
	}

	stateChange(uf: DropdownChangeEvent) {
		this.clearLocationValidationError();
		this.showLocationRequiredErrors = false;

		if (!this.shouldUseBrazilAddressValidation()) {
			this.selectedState = uf.value || '';
			this.clientData.stateCode = this.selectedState || '';
			this.citiesList = [];
			return;
		}

		this.selectedState = uf.value;
		this.clientData.stateCode = this.selectedState || '';
		this.selectedCity = "";
		this.clientData.city = "";
		this.disableCity = true;
		this.citiesList = [];

		// Reset FormControl para sincronizar com ngModel e manter consistência
		this.customerForm?.patchValue({
			optionCity: ''
		}, { emitEvent: false });
		this.resetCitySelectionValidationState();

		this.updateAddressControlsState();

		if (!this.selectedState) {
			return;
		}

		const cityRequestId = ++this.citySearchRequestId;

		this.addressService.getCities(this.selectedState)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(cities => {

				if (cityRequestId !== this.citySearchRequestId) {
					return;
				}

				this.citiesList = cities.map(city => ({ label: city, value: city }));
				this.disableCity = false;
				this.updateAddressControlsState();
			}, (error) => {

				if (cityRequestId !== this.citySearchRequestId) {
					return;
				}

				this.citiesList = [];
				this.disableCity = false;
				this.updateAddressControlsState();

				switch (error.status) {

					case 400:
					case 404:
					case 500:

						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.CITY.NOT.FOUND')
						});
						break;

					case 401:
						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
						});
						break;

					default:
						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
						});
						break;
				}

			});

	}

	private resetCitySelectionValidationState(): void {
		const cityControl = this.customerForm?.get('optionCity');
		if (!cityControl) {
			return;
		}

		cityControl.markAsPristine();
		cityControl.markAsUntouched();
	}

	searchingCities(stateUf: string, cityAddress: string, keepCityDisabledAfterLoad = false) {

		if (!this.shouldUseBrazilAddressValidation()) {
			this.selectedCity = cityAddress || '';
			this.clientData.city = this.selectedCity;
			this.citiesList = [];
			return;
		}

		const cityRequestId = ++this.citySearchRequestId;

		this.addressService.getCities(stateUf)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(cities => {

				if (cityRequestId !== this.citySearchRequestId) {
					return;
				}

				const formatCity = capitalizeFirstLetter(cityAddress);

				this.citiesList = cities.map(city => ({ label: city, value: city }));

				const cityExists = this.citiesList.find(cityFound => cityFound.value === formatCity);

				if (cityExists) {

					this.selectedCity = formatCity;
					this.clientData.city = this.selectedCity;
					this.disableCity = keepCityDisabledAfterLoad;
				} else {
					this.disableCity = false;
				}

				this.updateAddressControlsState();

			}, (error) => {

				if (cityRequestId !== this.citySearchRequestId) {
					return;
				}

				this.selectedCity = "";
				this.citiesList = [];
				this.disableCity = false;
				this.updateAddressControlsState();

				switch (error.status) {

					case 400:
					case 404:
					case 500:

						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.CITY.NOT.FOUND')
						});
						break;

					case 401:
						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
						});
						break;

					default:
						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
						});
						break;
				}

			});

	}

	cityChange(city: DropdownChangeEvent) {
		this.clearLocationValidationError();

		this.selectedCity = city.value;
		this.clientData.city = this.selectedCity || '';
	}

	buttonComeBack() {
		if (this.shouldValidateBrazilAddressBeforeComeBack()) {
			this.validateBrazilAddressConsistency(() => {
				this.persistClientDataBeforeComeBack();
				this.navigateToChoosePlan();
			});
			return;
		}

		this.persistClientDataBeforeComeBack();
		this.navigateToChoosePlan();
	}

	private shouldValidateBrazilAddressBeforeComeBack(): boolean {
		return this.shouldUseBrazilAddressValidation() && this.showRegistrationForm;
	}

	private validateBrazilAddressConsistency(onSuccess: () => void): void {
		this.clearLocationValidationError();
		this.refreshZipControlValidity();

		const zipCode = this.normalizeZip(this.clientData?.zip);
		const stateCode = this.clientData?.stateCode || this.selectedState || '';
		const cityName = this.clientData?.city || this.selectedCity || '';

		if (!zipCode) {
			this.showLocationRequiredErrors = true;
			this.customerForm.get('inputZipCode')?.markAsPending();
			this.focusFieldById('customerZipCode');
			return;
		}

		if (!stateCode) {
			this.showLocationRequiredErrors = true;
			this.customerForm.get('optionState')?.markAsTouched();
			this.focusFieldById('customerState');
			return;
		}

		if (!cityName) {
			this.showLocationRequiredErrors = true;
			this.customerForm.get('optionCity')?.markAsTouched();
			this.focusFieldById('customerCity');
			return;
		}

		const cepCityStateValidation: CepCityStateValidation = {
			cep: zipCode,
			city: cityName,
			state: stateCode
		};

		this.blockedUi = true;
		this.addressService.validateCepCityAndState(cepCityStateValidation)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (isValid: boolean) => {
					if (!isValid) {
						this.handleBackendAddressValidationFalse(zipCode, cityName, stateCode, onSuccess);
						return;
					}

					this.blockedUi = false;
					this.refreshZipControlValidity();
					onSuccess();
				},
				error: (error) => {
					this.blockedUi = false;
					const mappedError = this.validationErrorMapperService.mapBackendErrorToI18nKey(error);
					this.handleCepCityStateValidationFailure(mappedError.key);
				}
			});
	}

	private handleBackendAddressValidationFalse(
		zipCode: string,
		cityName: string,
		stateCode: string,
		onSuccess: () => void
	): void {
		const cachedAddressValidation = this.validationErrorMapperService.validateCepMatchesCityState(
			this.addressViaCep,
			cityName,
			stateCode
		);

		if (this.shouldProceedWithResolvedBrazilAddress(zipCode, cachedAddressValidation.isValid)) {
			this.blockedUi = false;
			onSuccess();
			return;
		}

		if (this.shouldProceedWithManualBrazilAddress(cachedAddressValidation.errorKey)) {
			this.blockedUi = false;
			onSuccess();
			return;
		}

		if (cachedAddressValidation.errorKey !== 'TRANSLATE.ERROR.CEP.INCOMPLETE.DATA') {
			this.blockedUi = false;
			this.handleCepCityStateValidationFailure(cachedAddressValidation.errorKey || 'TRANSLATE.ERROR.ADDRESS.INVALID');
			return;
		}

		this.addressService.getViaCep(zipCode)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (response: ViaCep) => {
					this.addressViaCep = response;
					const refreshedAddressValidation = this.validationErrorMapperService.validateCepMatchesCityState(
						response,
						cityName,
						stateCode
					);

					this.blockedUi = false;

					if (this.shouldProceedWithResolvedBrazilAddress(zipCode, refreshedAddressValidation.isValid)) {
						onSuccess();
						return;
					}

					if (this.shouldProceedWithManualBrazilAddress(refreshedAddressValidation.errorKey)) {
						onSuccess();
						return;
					}

					this.handleCepCityStateValidationFailure(refreshedAddressValidation.errorKey || 'TRANSLATE.ERROR.ADDRESS.INVALID');
				},
				error: () => {
					this.blockedUi = false;

					if (this.shouldProceedWithManualBrazilAddress('TRANSLATE.ERROR.CEP.NOT.FOUND')) {
						onSuccess();
						return;
					}

					this.handleCepCityStateValidationFailure('TRANSLATE.ERROR.CEP.NOT.FOUND');
				}
			});
	}

	private handleCepCityStateValidationFailure(errorKey: string): void {
		this.locationValidationErrorKey = errorKey;
		this.showLocationRequiredErrors = false;

		this.messageService.add({
			severity: 'error',
			summary: this.translateService.instant('TRANSLATE.ERROR'),
			detail: this.translateService.instant(errorKey)
		});

		switch (errorKey) {
			case 'TRANSLATE.ERROR.CEP.STATE.MISMATCH':
				this.customerForm.get('optionState')?.markAsTouched();
				this.focusFieldById('customerState');
				break;

			case 'TRANSLATE.ERROR.CEP.CITY.MISMATCH':
				this.customerForm.get('optionCity')?.markAsTouched();
				this.focusFieldById('customerCity');
				break;

			default:
				this.customerForm.get('inputZipCode')?.markAsPending();
				this.focusFieldById('customerZipCode');
				break;
		}
	}

	private clearLocationValidationError(): void {
		this.locationValidationErrorKey = null;
	}

	private refreshZipControlValidity(): void {
		this.customerForm.get('inputZipCode')?.updateValueAndValidity({ emitEvent: false });
	}

	private shouldProceedWithResolvedBrazilAddress(zipCode: string, localAddressIsValid: boolean): boolean {
		const resolvedZipCode = this.normalizeZip(this.addressViaCep?.postalCode);
		return Boolean(resolvedZipCode) && resolvedZipCode === zipCode && localAddressIsValid;
	}

	private shouldProceedWithManualBrazilAddress(errorKey?: string): boolean {
		const fallbackErrorKeys = ['TRANSLATE.ERROR.CEP.INCOMPLETE.DATA', 'TRANSLATE.ERROR.CEP.NOT.FOUND'];
		if (!errorKey || !fallbackErrorKeys.includes(errorKey)) {
			return false;
		}

		return Boolean(
			this.normalizeZip(this.clientData?.zip)
			&& (this.clientData?.address || '').trim()
			&& (this.clientData?.number || '').trim()
			&& (this.clientData?.district || '').trim()
			&& (this.clientData?.stateCode || this.selectedState || '').trim()
			&& (this.clientData?.city || this.selectedCity || '').trim()
		);
	}

	shouldShowStateValidationError(): boolean {
		return this.locationValidationErrorKey === 'TRANSLATE.ERROR.CEP.STATE.MISMATCH';
	}

	shouldShowCityValidationError(): boolean {
		return this.locationValidationErrorKey === 'TRANSLATE.ERROR.CEP.CITY.MISMATCH';
	}

	shouldShowZipValidationError(): boolean {
		return this.locationValidationErrorKey !== null
			&& !this.shouldShowStateValidationError()
			&& !this.shouldShowCityValidationError();
	}

	getLocationValidationMessage(): string {
		switch (this.locationValidationErrorKey) {
			case 'TRANSLATE.ERROR.CEP.STATE.MISMATCH':
				if (this.translateService.currentLang?.startsWith('es')) {
					return 'Seleccione el estado correcto para el código postal informado.';
				}

				if (this.translateService.currentLang?.startsWith('en')) {
					return 'Select the state that matches the informed ZIP code.';
				}

				return 'Selecione o estado correto para o CEP informado.';

			case 'TRANSLATE.ERROR.CEP.CITY.MISMATCH':
				if (this.translateService.currentLang?.startsWith('es')) {
					return 'Seleccione la ciudad correcta para el código postal informado.';
				}

				if (this.translateService.currentLang?.startsWith('en')) {
					return 'Select the city that matches the informed ZIP code.';
				}

				return 'Selecione a cidade correta para o CEP informado.';

			default:
				return this.locationValidationErrorKey
					? this.translateService.instant(this.locationValidationErrorKey)
					: '';
		}
	}

	private persistClientDataBeforeComeBack(): void {
		const normalizedDocument = this.normalizeDocument(this.informedCustomerDocument);

		if (this.showRegistrationForm && normalizedDocument) {
			this.clientData.nationalId = normalizedDocument;
			if (!this.clientData.countryCode) {
				this.clientData.countryCode = this.systemCountry;
			}
			this.clientDataService.setClientData(this.clientData);
		} else if (normalizedDocument) {
			const draftClientData = new ClientData();
			draftClientData.countryCode = this.systemCountry;
			draftClientData.nationalId = normalizedDocument;
			this.clientDataService.setClientData(draftClientData);
			this.clientDataService.clearConsultState();
		}
	}

	private navigateToChoosePlan(): void {
		this.router.navigate(['choose-plan', this.saleChannel], { skipLocationChange: true, replaceUrl: false });
	}

	buttonConsult() {
		this.showLocationRequiredErrors = false;

		if (this.consultDebounceHandle) {
			clearTimeout(this.consultDebounceHandle);
			this.consultDebounceHandle = null;
		}

		console.log("[client-data] inicio buttonConsult -> Client Data country code: ", this.clientData.countryCode);
		console.log("[client-data] inicio buttonConsult -> Client Data country code via service: ", this.clientDataService.getClientData().countryCode);

		this.clearCustomerInformation();
		this.clearAddressSearches();

		this.validCustomerDocument = this.informedCustomerDocument;

		if (!this.validCustomerDocument) {

			this.blockedUi = false;
			this.validCustomerDocument = null;
			this.emptyCustomerDocument = true;
			this.showRegistrationForm = false;
			this.clientDataService.clearConsultState();
			return;
		}
		if (this.validCustomerDocument.length === 0) {

			this.blockedUi = false;
			this.emptyCustomerDocument = true;
			this.showRegistrationForm = false;
			this.clientDataService.clearConsultState();
			return;
		}

		console.log("[client-data] -> buttonConsult -> switch this.systemCountry: ", this.systemCountry);
		console.log("[client-data] -> buttonConsult -> switch this.clientData. via service: ", this.clientDataService.getClientData().countryCode);
		switch (this.systemCountry) {

			case ConstantsFordProtect.ARG:

				if (this.validCustomerDocument.length >= 7 || this.validCustomerDocument.length === 11) {

					this.markDocumentAsConsulted(this.validCustomerDocument);
					this.showRegistrationForm = true;
					this.blockedUi = true;
					this.clientData.nationalId = this.validCustomerDocument;
					this.searchCustomers(this.systemCountry, this.clientData.nationalId);
				} else {

					this.blockedUi = false;
					this.validCustomerDocument = null;
					this.showRegistrationForm = false;
					this.clientDataService.clearConsultState();
				}
				break;

			case ConstantsFordProtect.CHL:

				if (this.validCustomerDocument.length === 9) {

					this.markDocumentAsConsulted(this.validCustomerDocument);
					this.showRegistrationForm = true;
					this.blockedUi = true;
					this.clientData.nationalId = this.validCustomerDocument;
					this.searchCustomers(this.systemCountry, this.clientData.nationalId);
				} else {

					this.blockedUi = false;
					this.validCustomerDocument = null;
					this.showRegistrationForm = false;
					this.clientDataService.clearConsultState();
				}

				break;

			case ConstantsFordProtect.COL:

				if (this.validCustomerDocument.length >= 4 || this.validCustomerDocument.length === 10) {

					this.markDocumentAsConsulted(this.validCustomerDocument);
					this.showRegistrationForm = true;
					this.blockedUi = true;
					this.clientData.nationalId = this.validCustomerDocument;
					this.searchCustomers(this.systemCountry, this.clientData.nationalId);
				} else {

					this.blockedUi = false;
					this.validCustomerDocument = null;
					this.showRegistrationForm = false;
					this.clientDataService.clearConsultState();
				}
				break;

			default:

				if (this.validCustomerDocument.length === 11) {

					if (validationCpf(this.validCustomerDocument)) {

						this.markDocumentAsConsulted(this.validCustomerDocument);
						this.showRegistrationForm = true;
						this.blockedUi = true;
						this.clientData.nationalId = this.validCustomerDocument;
						this.searchCustomers(this.systemCountry, this.clientData.nationalId);
					} else {

						this.blockedUi = false;
						this.invalidCpf = null;
						this.showRegistrationForm = false;
						this.clientDataService.clearConsultState();
					}

				} else if (this.validCustomerDocument.length === 14) {

					if (validationCnpj(this.validCustomerDocument)) {

						this.markDocumentAsConsulted(this.validCustomerDocument);
						this.showRegistrationForm = true;
						this.blockedUi = true;
						this.clientData.nationalId = this.validCustomerDocument;
						this.searchCustomers(this.systemCountry, this.clientData.nationalId);
					} else {

						this.blockedUi = false;
						this.invalidCnpj = null;
						this.showRegistrationForm = false;
						this.clientDataService.clearConsultState();
					}

				} else {

					this.blockedUi = false;
					this.invalidDocument = null;
					this.showRegistrationForm = false;
					this.clientDataService.clearConsultState();
				}

				break;
		}

		console.log("[client-data] fim buttonConsult -> Client Data country code: ", this.clientData.countryCode);
		console.log("[client-data] fim buttonConsult -> Client Data country code via service: ", this.clientDataService.getClientData().countryCode);

	}

	inputValidatingClientDocument(event?: FocusEvent) {
		void event;

		this.validCustomerDocument = "";
		this.invalidCpf = "";
		this.invalidCnpj = "";
		this.invalidDocument = "";
		this.emptyCustomerDocument = false;

		this.validCustomerDocument = this.informedCustomerDocument;

		if (this.isSameDocumentAsLoaded(this.validCustomerDocument)) {
			return;
		}

		if (this.validCustomerDocument === undefined) {

			this.validCustomerDocument = null;
			this.emptyCustomerDocument = false;
			this.showRegistrationForm = false;
			return;
		}
		if (this.validCustomerDocument.length === 0) {

			this.emptyCustomerDocument = true;
			this.showRegistrationForm = false;
			return;
		}

		switch (this.systemCountry) {

			case ConstantsFordProtect.ARG:

				if (this.validCustomerDocument.length >= 7 || this.validCustomerDocument.length === 11) {

					this.clientData.nationalId = this.validCustomerDocument;
				} else {

					this.blockedUi = false;
					this.validCustomerDocument = null;
				}
				break;

			case ConstantsFordProtect.CHL:

				if (this.validCustomerDocument.length === 9) {

					this.clientData.nationalId = this.validCustomerDocument;
				} else {

					this.blockedUi = false;
					this.validCustomerDocument = null;
				}

				break;

			case ConstantsFordProtect.COL:

				if (this.validCustomerDocument.length >= 4 || this.validCustomerDocument.length === 10) {

					this.clientData.nationalId = this.validCustomerDocument;
				} else {

					this.blockedUi = false;
					this.validCustomerDocument = null;
				}
				break;

			default:

				if (this.validCustomerDocument.length === 11) {

					if (validationCpf(this.validCustomerDocument)) {

						this.clientData.nationalId = this.validCustomerDocument;
					} else {
						this.blockedUi = false;
						this.invalidCpf = null;
					}

				} else if (this.validCustomerDocument.length === 14) {

					if (validationCnpj(this.validCustomerDocument)) {

						this.clientData.nationalId = this.validCustomerDocument;

					} else {

						this.blockedUi = false;
						this.invalidCnpj = null;
					}

				} else {

					this.invalidCpf = "";
					this.invalidCnpj = "";
					this.invalidDocument = null;
					this.blockedUi = false;
				}
				break;
		}

	}

	onCustomerDocumentChange(value: string) {
		this.informedCustomerDocument = value;
		this.updateCustomerNameValidation();
		this.showRegistrationForm = false;
		this.showLocationRequiredErrors = false;
		this.clearConsultStateIfDocumentChanged(value);

		if (this.normalizeDocument(value)) {
			return;
		}

		this.showRegistrationForm = false;
		this.lastLoadedOrConsultedDocument = "";
		this.clearCustomerInformation();
		this.clearAddressSearches();
	}

	buttonNext() {

		console.log("[client-data] -> inicio buttonNext -> this.clientData.countryCode: ", this.clientData.countryCode);
		console.log("[client-data] -> inicio buttonNext -> this.clientData. via service: ", this.clientDataService.getClientData().countryCode);
		if (!this.informedCustomerDocument || this.informedCustomerDocument.trim() === "") {
			this.emptyCustomerDocument = true;
			this.onFocusInput();
			return;
		}

		if (this.systemCountry === ConstantsFordProtect.BRA) {

			if (this.informedCustomerDocument.length === 11) {
				if (!validationCpf(this.informedCustomerDocument)) {
					this.onFocusInput();
					this.blockedUi = false;
					this.invalidCpf = null;
					return;
				}
			} else if (this.informedCustomerDocument.length === 14) {
				if (!validationCnpj(this.informedCustomerDocument)) {
					this.onFocusInput();
					this.blockedUi = false;
					this.invalidCnpj = null;
					return;
				}
			} else {
				this.onFocusInput();
				this.invalidCpf = "";
				this.invalidCnpj = "";
				this.invalidDocument = null;
				this.blockedUi = false;
				return;
			}
		}

		this.showLocationRequiredErrors = true;
		this.validateInputs();
		this.onFocusInput();
		if (this.customerForm.valid) {
			if (this.shouldUseBrazilAddressValidation()) {
				this.validateBrazilAddressConsistency(() => this.proceedToContractSummary());
				return;
			}

			this.proceedToContractSummary();
		}

		console.log("[client-data] -> fim buttonNext -> this.clientData.countryCode: ", this.clientData.countryCode);
		console.log("[client-data] -> fim buttonNext -> this.clientData. via service: ", this.clientDataService.getClientData().countryCode);

	}

	private proceedToContractSummary(): void {
		this.clientData.nationalId = this.informedCustomerDocument;

		if (this.shouldUseBrazilAddressValidation() && this.clientData.stateCode && this.clientData.city) {
			this.locationLockedByDocument = true;
			this.lockedLocationZip = this.normalizeZip(this.clientData?.zip);
			this.disableState = true;
			this.disableCity = true;
			this.clientDataService.setLocationLockState(true, this.lockedLocationZip);
			this.updateAddressControlsState();
		}

		// Normalize client data before saving: Title Case for text fields, lowercase for email
		this.normalizeClientData();
		this.clientDataService.setClientData(this.clientData);
		if (this.saleChannel === SaleChannel.EXTERNAL) {
			this.router.navigate(['contract-summary-external', this.saleChannel], { skipLocationChange: true, replaceUrl: false });
		} else {
			this.router.navigate(['contract-summary', this.saleChannel], { skipLocationChange: true, replaceUrl: false });
		}
	}

	validateInputs() {
		if (this.customerForm.get('inputName')?.invalid) {

			this.customerForm.get('inputName')?.markAsPending();
		}
		if (this.customerForm.get('inputLastName')?.invalid) {

			this.customerForm.get('inputLastName')?.markAsPending();
		}
		if (this.customerForm.get('inputPhoneNumber')?.invalid) {

			this.customerForm.get('inputPhoneNumber')?.markAsPending();
		}
		if (this.customerForm.get('inputEmail')?.invalid) {

			this.customerForm.get('inputEmail')?.markAsPending();
		}
		if (this.customerForm.get('inputZipCode')?.invalid) {

			this.customerForm.get('inputZipCode')?.markAsPending();
		}
		if (this.customerForm.get('inputAddress')?.invalid) {

			this.customerForm.get('inputAddress')?.markAsPending();
		}
		if (this.customerForm.get('inputNumber')?.invalid) {

			this.customerForm.get('inputNumber')?.markAsPending();
		}
		if (this.customerForm.get('inputDistrict')?.invalid) {

			this.customerForm.get('inputDistrict')?.markAsPending();
		}
		if (this.systemCountry !== ConstantsFordProtect.BRA && this.customerForm.get('inputCity')?.invalid) {

			this.customerForm.get('inputCity')?.markAsPending();
		}
		if (this.systemCountry !== ConstantsFordProtect.BRA && this.customerForm.get('inputState')?.invalid) {

			this.customerForm.get('inputState')?.markAsPending();
		}
		if (!this.selectedState && this.systemCountry === ConstantsFordProtect.BRA) {

			this.customerForm.get('optionState')?.markAsTouched();
			this.selectedState = "";
		}
		if (!this.selectedCity && this.systemCountry === ConstantsFordProtect.BRA) {

			this.customerForm.get('optionCity')?.markAsTouched();
			this.selectedCity = "";
		}
		if (this.systemCountry === ConstantsFordProtect.BRA && this.customerForm.get('inputBirthDate')?.invalid) {

			this.customerForm.get('inputBirthDate')?.markAsPending();
		}
		if (this.systemCountry !== ConstantsFordProtect.BRA) {
			this.customerForm.get('inputBirthDate')?.setErrors(null);
		}
	}

	getFieldValidationMessage(controlName: string, requiredFallbackKey: string): string {
		if (!this.customerForm) {
			return requiredFallbackKey;
		}

		const control = this.customerForm.get(controlName);
		if (!control || !control.errors) {
			return requiredFallbackKey;
		}

		if (control.hasError('required')) {
			return requiredFallbackKey;
		}

		if (control.hasError('whitespace')) {
			return requiredFallbackKey;
		}

		if (control.hasError('email')) {
			return 'TRANSLATE.INVALID.EMAIL.PLEASE.FILL.CORRECTLY';
		}

		if (control.hasError('pattern')) {
			return this.getPatternValidationMessageKey(controlName);
		}

		if (control.hasError('minlength') || control.hasError('maxlength')) {
			return this.getLengthValidationMessageKey(controlName, requiredFallbackKey);
		}

		return requiredFallbackKey;
	}

	private getPatternValidationMessageKey(controlName: string): string {
		switch (controlName) {
			case 'inputName':
				return this.shouldAllowDigitsInCustomerName()
					? 'TRANSLATE.CLIENT.DATA.ONLY.LETTERS.NUMBERS.ALLOWED'
					: 'TRANSLATE.CLIENT.DATA.ONLY.LETTERS.ALLOWED';

			case 'inputLastName':
			case 'inputCity':
			case 'inputState':
				return 'TRANSLATE.CLIENT.DATA.ONLY.LETTERS.ALLOWED';

			case 'inputPhoneNumber':
			case 'inputZipCode':
				return 'TRANSLATE.CLIENT.DATA.ONLY.NUMBERS.ALLOWED';

			case 'inputAddress':
			case 'inputDistrict':
				return 'TRANSLATE.CLIENT.DATA.INVALID.SPECIAL.CHARACTERS';

			case this.systemCountry === ConstantsFordProtect.BRA &&'inputNumber':
				return 'TRANSLATE.CLIENT.DATA.ONLY.NUMBERS.ALLOWED';

			default:
				return 'TRANSLATE.CLIENT.DATA.INVALID.FIELD.FORMAT';
		}
	}

	private getLengthValidationMessageKey(controlName: string, requiredFallbackKey: string): string {
		switch (controlName) {
			case 'inputName':
				return 'TRANSLATE.CLIENT.DATA.NAME.LENGTH.RULE';

			case 'inputLastName':
				return 'TRANSLATE.CLIENT.DATA.LAST.NAME.LENGTH.RULE';

			case 'inputPhoneNumber':
				if (this.systemCountry === ConstantsFordProtect.BRA || this.systemCountry === ConstantsFordProtect.CHL) {
					return 'TRANSLATE.CLIENT.DATA.PHONE.LENGTH.EXACT.RULE';
				}
				return 'TRANSLATE.CLIENT.DATA.PHONE.LENGTH.RANGE.RULE';

			case 'inputZipCode':
				if (this.systemCountry === ConstantsFordProtect.BRA) {
					return 'TRANSLATE.CLIENT.DATA.ZIP.LENGTH.BRA.RULE';
				}
				if (this.systemCountry === ConstantsFordProtect.ARG) {
					return 'TRANSLATE.CLIENT.DATA.ZIP.LENGTH.ARG.RULE';
				}
				if (this.systemCountry === ConstantsFordProtect.CHL) {
					return 'TRANSLATE.CLIENT.DATA.ZIP.LENGTH.CHL.RULE';
				}
				return 'TRANSLATE.CLIENT.DATA.ZIP.LENGTH.COL.RULE';

			case 'inputNumber':
				return 'TRANSLATE.CLIENT.DATA.NUMBER.LENGTH.RULE';

			case 'inputCity':
				return 'TRANSLATE.CLIENT.DATA.CITY.LENGTH.RULE';

			case 'inputState':
				return this.systemCountry === ConstantsFordProtect.BRA
					? 'TRANSLATE.CLIENT.DATA.STATE.LENGTH.BRA.RULE'
					: 'TRANSLATE.CLIENT.DATA.STATE.LENGTH.NON.BRA.RULE';

			default:
				return requiredFallbackKey;
		}
	}

	private focusFieldById(elementId: string): void {
		setTimeout(() => {
			const host = document.getElementById(elementId) as HTMLElement | null;
			if (!host) {
				return;
			}

			const focusableChild = host.querySelector('input, textarea, .p-dropdown-label, .p-dropdown-trigger, [tabindex]') as HTMLElement | null;
			const target = focusableChild ?? host;

			target.scrollIntoView({ behavior: 'smooth', block: 'center' });
			target.focus();
		}, 100);
	}

	private focusFirstInvalidField(): void {
		if (
			!this.informedCustomerDocument ||
			this.emptyCustomerDocument ||
			this.validCustomerDocument === null ||
			this.invalidCpf === null ||
			this.invalidCnpj === null ||
			this.invalidDocument === null
		) {
			this.focusFieldById('customerDocument');
			return;
		}

		const fieldOrder: { controlName: string; elementId: string }[] = [
			{ controlName: 'inputName', elementId: 'nameOrCompanyName' },
			{ controlName: 'inputLastName', elementId: 'surName' },
			{ controlName: 'inputPhoneNumber', elementId: 'customerPhoneNumber' },
			{ controlName: 'inputEmail', elementId: 'customerEmail' },
			{ controlName: 'inputZipCode', elementId: 'customerZipCode' },
			{ controlName: 'inputAddress', elementId: 'customerAddress' },
			{ controlName: 'inputNumber', elementId: 'customerHouseNumber' },
			{ controlName: 'inputDistrict', elementId: 'customerDistrict' }
		];

		if (!this.shouldUseBrazilAddressValidation()) {
			fieldOrder.push(
				{ controlName: 'inputCity', elementId: 'customerCity' },
				{ controlName: 'inputState', elementId: 'customerState' }
			);
		}

		if (this.systemCountry === ConstantsFordProtect.BRA) {
			fieldOrder.splice(2, 0, { controlName: 'inputBirthDate', elementId: 'customerDateOfBirth' });
		}

		for (const field of fieldOrder) {
			const control = this.customerForm.get(field.controlName);
			if (control && !control.disabled && (control.invalid || control.pending)) {
				this.focusFieldById(field.elementId);
				return;
			}
		}

		if (this.shouldUseBrazilAddressValidation()) {
			if (!this.selectedState) {
				this.focusFieldById('customerState');
				return;
			}

			if (!this.selectedCity) {
				this.focusFieldById('customerCity');
			}
		}
	}

	//onFocus nos inputs com problemas de validação
	onFocusInput() {
		this.focusFirstInvalidField();
	}

	searchCustomers(country: string, nationalId: string) {

		this.clientDataService.getCustomers(country, nationalId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((response: ClientData) => {

				if (response === null || response.nationalId === null || response.nationalId === "") {

					console.log("[client-data] -> searchCustomers -> cliente não encontrado");
					console.log("[client-data] -> searchCustomers -> response.nationalId: ", response.nationalId);
					this.clearCustomerInformation();
					this.blockedUi = false;

					this.messageService.add({
						severity: 'warn',
						summary: this.translateService.instant('TRANSLATE.ALERT'),
						detail: this.translateService.instant('TRANSLATE.WE.COULD.NOT.FIND.RECORD.WITH.DOCUMENT')
					});

				} else {

					console.log("[client-data] -> searchCustomers -> cliente encontrado");
					this.blockedUi = false;
					this.clientData = response;
					this.clientData.nationalId = response.nationalId;
					this.clientData.firstname = response.firstname ? response.firstname.replace(/[.]/g, '') : '';
					this.clientData.lastname = response.lastname;
					this.clientData.customerSocialName = response.customerSocialName;
					this.clientData.birthDate = response.birthDate;
					this.clientData.phone1 = response.phone1 ? response.phone1.replace(/[-()]/g, '') : '';
					this.clientData.email1 = response.email1;
					this.clientData.zip = response.zip ? response.zip.replace(/[-]/g, '') : '';
					this.clientData.address = response.address;
					this.clientData.number = response.number;
					this.clientData.district = response.district;

					if (response.complement === null) {

						this.clientData.complement = "";
					} else {

						this.clientData.complement = response.complement;
					}

					if (country === ConstantsFordProtect.BRA) {

						this.clientData.stateCode = response.stateCode;
						this.selectedState = response.stateCode;

						if (response.stateCode && response.city) {
							const shouldLockLocation = Boolean(this.normalizeZip(response.zip));
							if (shouldLockLocation) {
								this.lockLocationFromLoadedDocument();
							}

							this.searchingCities(response.stateCode, response.city, shouldLockLocation);
						}

					} else {

						this.clientData.city = response.city;
						this.clientData.stateCode = response.stateCode;
					}

					console.log("[client-data] -> searchCustomers -> response.countryCode: ", response.countryCode);
					if (response.countryCode === null) {

						this.clientData.countryCode = this.systemCountry;
						console.log("[client-data] -> searchCustomers -> response.countryCode === null -> country -> ", this.clientData.countryCode);
					} else {

						this.clientData.countryCode = response.countryCode;
						console.log("[client-data] -> searchCustomers -> response.countryCode !== null -> country -> ", this.clientData.countryCode);
					}

					this.lastLoadedOrConsultedDocument = this.normalizeDocument(this.clientData.nationalId);

				}

			}, (error) => {

				this.clearCustomerInformation();
				this.blockedUi = false;

				switch (error.status) {

					case 400:
					case 404:
					case 500:

						this.messageService.add({
							severity: 'warn',
							summary: this.translateService.instant('TRANSLATE.ALERT'),
							detail: this.translateService.instant('TRANSLATE.WE.COULD.NOT.FIND.RECORD.WITH.DOCUMENT')
						});
						break;

					case 401:
						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
						});
						break;

					default:
						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
						});
						break;
				}

			});
	}

	clearCustomerInformation() {

		this.clientData = new ClientData();
		this.clientData.countryCode = this.systemCountry;
		this.clientData.nationalId = "";
		this.clientData.firstname = "";
		this.clientData.lastname = "";
		this.clientData.customerSocialName = "";
		this.clientData.birthDate = null!;
		this.clientData.phone1 = "";
		this.clientData.email1 = "";
		this.clientData.zip = "";
		this.clientData.address = "";
		this.clientData.number = "";
		this.clientData.district = "";
		this.clientData.complement = "";
		this.clientData.city = "";
		this.clientData.stateCode = "";
		this.validCustomerDocument = "";
		this.invalidCpf = "";
		this.invalidCnpj = "";
		this.invalidDocument = "";
		this.emptyCustomerDocument = false;
		this.locationLockedByDocument = false;
		this.lockedLocationZip = '';
		this.setZipLookupInProgress(false);
		this.cancelZipLookupDebounce();
		this.clientDataService.clearLocationLockState();
	}

	clearAddressSearches(invalidatePendingRequests = true) {

		if (invalidatePendingRequests) {
			this.invalidatePendingAddressLookups();
		}

		this.locationLockedByDocument = false;
		this.lockedLocationZip = '';
		this.clientDataService.clearLocationLockState();

		// Limpar modelo de dados - seguindo padrão de stateChange
		this.clientData.countryCode = "";
		this.clientData.address = "";
		this.clientData.district = "";
		this.clientData.number = "";
		this.clientData.complement = "";
		this.clientData.stateCode = "";
		this.clientData.city = "";
		this.selectedCity = "";
		this.selectedState = "";
		this.citiesList = [];
		this.disableCity = true;
		this.disableState = false;
		this.addressViaCep = new ViaCep();

		// Limpa o dropdown do Estado via patchValue (controle habilitado → re-render imediato)
		this.customerForm?.patchValue({ optionState: '' }, { emitEvent: false });
		this.customerForm?.get('optionState')?.markAsPristine();
		this.customerForm?.get('optionState')?.markAsUntouched();

		// Força recriação do dropdown Cidade habilitando o controle antes de destruir
		// (sem isso, PrimeNG ignora a mudança de valor quando o controle já está disabled)
		this.resetCityDropdownView();

		this.updateAddressControlsState();
	}

	private normalizeDocument(document: string | null | undefined): string {
		return (document || '').trim();
	}

	private markDocumentAsConsulted(document: string): void {
		const normalizedDocument = this.normalizeDocument(document);
		this.lastLoadedOrConsultedDocument = normalizedDocument;
		this.clientDataService.setConsultState(true, normalizedDocument);
	}

	private clearConsultStateIfDocumentChanged(document: string | null | undefined): void {
		const consultState = this.clientDataService.getConsultState();
		if (!consultState.performed) {
			return;
		}

		const normalizedDocument = this.normalizeDocument(document);
		if (!normalizedDocument || normalizedDocument !== consultState.document) {
			this.clientDataService.clearConsultState();
			this.lastLoadedOrConsultedDocument = "";
		}
	}

	private isSameDocumentAsLoaded(document: string | null | undefined): boolean {
		const normalizedDocument = this.normalizeDocument(document);
		if (!normalizedDocument) {
			return false;
		}

		return normalizedDocument === this.lastLoadedOrConsultedDocument;
	}

	private isDocumentReadyForRegistration(document: string | null | undefined): boolean {
		const normalizedDocument = this.normalizeDocument(document);

		if (!normalizedDocument) {
			return false;
		}

		if (this.isSameDocumentAsLoaded(normalizedDocument)) {
			return true;
		}

		switch (this.systemCountry) {
			case ConstantsFordProtect.ARG:
				return normalizedDocument.length >= 7 || normalizedDocument.length === 11;
			case ConstantsFordProtect.CHL:
				return normalizedDocument.length === 9;
			case ConstantsFordProtect.COL:
				return normalizedDocument.length >= 4 || normalizedDocument.length === 10;
			default:
				if (normalizedDocument.length === 11) {
					return validationCpf(normalizedDocument);
				}

				if (normalizedDocument.length === 14) {
					return validationCnpj(normalizedDocument);
				}

				return false;
		}
	}

	private normalizeClientData(): void {
		const normalizeText = (value: string | null | undefined): string => value ? value.replace(/\s+/g, ' ').trim() : '';

		// Apply Title Case to text fields
		if (this.clientData.firstname) {
			this.clientData.firstname = capitalizeFirstLetter(normalizeText(this.clientData.firstname));
		}
		if (this.clientData.lastname) {
			this.clientData.lastname = capitalizeFirstLetter(normalizeText(this.clientData.lastname));
		}
		if (this.clientData.customerSocialName) {
			this.clientData.customerSocialName = capitalizeFirstLetter(normalizeText(this.clientData.customerSocialName));
		}
		if (this.clientData.address) {
			this.clientData.address = capitalizeFirstLetter(normalizeText(this.clientData.address));
		}
		if (this.clientData.complement) {
			this.clientData.complement = capitalizeFirstLetter(normalizeText(this.clientData.complement));
		}
		if (this.clientData.district) {
			this.clientData.district = capitalizeFirstLetter(normalizeText(this.clientData.district));
		}
		if (this.clientData.city) {
			this.clientData.city = capitalizeFirstLetter(normalizeText(this.clientData.city));
		}

		// Convert email to lowercase
		if (this.clientData.email1) {
			this.clientData.email1 = this.clientData.email1.toLowerCase().trim();
		}
	}

}
